import { InfoOutlined } from "@mui/icons-material";
import { Grid, useTheme } from "@mui/material";
import AppliedFiltersChips from "components/ApplicationFilters/FiltersAndChips/AppliedFiltersChips";
import AcxChip from "components/UI/AcxChip";
import _ from "lodash";
import { observer } from "mobx-react";
import { useMemo, useRef } from "react";
import {
    ApplicationFilterItemsStore,
    type FilterKey,
    type IApplicationFilterItem,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import BaseObjectStore from "../BaseObjectStore";
import { GroupedByChip, useGroupedByChipText } from "./GroupedByChip";
import {
    OBJECT_CALL_DURATION_FILTER_KEY,
    OBJECT_DATE_TYPE_FILTER_KEY,
    OBJECT_MESSAGE_COUNT_FILTER_KEY,
    getObjectFilterKeysFromFilters,
} from "./objectFilterKeys";

type Props = {
    store: BaseObjectStore<any, any>;
};

const ObjectFiltersChips = observer(({ store }: Props) => {
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
    const theme = useTheme();
    const groupedByChipText = useGroupedByChipText(store);

    const objectFilterStore = store.objectFiltersStore;
    const currentObjectFilters = objectFilterStore?.toFilterObject() ?? null;
    const lastAppliedObjectFiltersRaw = store.getLastAppliedObjectFiltersSnapshot();
    // Question Result rows are stripped by module permission, so this answer can
    // change without the filters themselves changing. Fold the module access key
    // into the cache signature, or the notice would go on disagreeing with the
    // chips once the catalog lands.
    const permissionNoticeSignature = `${JSON.stringify(
        lastAppliedObjectFiltersRaw ?? null,
    )}::${applicationFilterValuesStore.questionResultModuleAccessKey}`;
    const permissionNoticeCacheRef = useRef<{
        signature: string;
        removedInaccessibleFilters: boolean;
    } | null>(null);

    if (
        !permissionNoticeCacheRef.current ||
        permissionNoticeCacheRef.current.signature !== permissionNoticeSignature
    ) {
        permissionNoticeCacheRef.current = {
            signature: permissionNoticeSignature,
            removedInaccessibleFilters: lastAppliedObjectFiltersRaw
                ? applicationFilterItemsStore.stripInaccessibleFilters(
                      _.cloneDeep(lastAppliedObjectFiltersRaw),
                  ).removedInaccessibleFilters
                : false,
        };
    }

    const hasPermissionStrippedObjectFilters =
        permissionNoticeCacheRef.current?.removedInaccessibleFilters ?? false;
    // Compare the draft against the raw committed filters rather than the
    // sanitized ones. This comparison decides which chips render as "Not
    // applied", and it is meant to catch edits the user has not applied yet — a
    // row dropped for permissions is not one of those. The chips themselves hide
    // inaccessible rows, so such a row can never surface as a visible mismatch;
    // comparing against the sanitized set instead grayed out every remaining
    // Question Result chip whenever any sibling row was stripped.
    const appliedObjectFiltersBaseline = store.isRefreshing
        ? currentObjectFilters
        : lastAppliedObjectFiltersRaw;
    const hasDraftDateOverride = store.objectFilterDateOverride;
    const hasDraftDateTypeOverride = store.objectFilterDateTypeOverride;
    const hasDraftCallDurationOverride = store.objectFilterCallDurationOverride;
    const hasDraftMessageCountOverride = store.objectFilterMessageCountOverride;
    const hasNonDefaultCallDuration =
        (currentObjectFilters?.minCallDuration ?? 0) > 0 ||
        (currentObjectFilters?.maxCallDuration ?? 0) > 0;
    const hasNonDefaultMessageCount =
        (currentObjectFilters?.minMessageCount ?? 0) > 0 ||
        (currentObjectFilters?.maxMessageCount ?? 0) > 0;

    const objectChipAllowedFilterKeys = useMemo<FilterKey[]>(
        () => [
            ...(hasDraftDateOverride
                ? (["dateRange"] as FilterKey[])
                : []),
            ...getObjectFilterKeysFromFilters(currentObjectFilters)
                .filter(
                    (key) =>
                        key !== OBJECT_CALL_DURATION_FILTER_KEY ||
                        hasDraftCallDurationOverride ||
                        hasNonDefaultCallDuration,
                )
                .filter(
                    (key) =>
                        key !== OBJECT_MESSAGE_COUNT_FILTER_KEY ||
                        hasDraftMessageCountOverride ||
                        hasNonDefaultMessageCount,
                )
                .map((key) => key as FilterKey),
            ...(hasDraftDateTypeOverride
                ? ([OBJECT_DATE_TYPE_FILTER_KEY] as FilterKey[])
                : []),
        ],
        [
            currentObjectFilters,
            hasDraftCallDurationOverride,
            hasDraftDateOverride,
            hasDraftDateTypeOverride,
            hasDraftMessageCountOverride,
            hasNonDefaultCallDuration,
            hasNonDefaultMessageCount,
        ],
    );

    const objectChipFilterItems = useMemo<IApplicationFilterItem[]>(() => {
        const allowedKeySet = new Set(objectChipAllowedFilterKeys);
        return applicationFilterItemsStore.filterGroups
            .flatMap((group) => group.items)
            .filter((item) => allowedKeySet.has(item.filterKey))
            .filter((item) =>
                applicationFilterItemsStore.canAccessFilterItem(item),
            );
    }, [applicationFilterItemsStore, objectChipAllowedFilterKeys]);

    const permissionNoticeChip = useMemo(
        () =>
            hasPermissionStrippedObjectFilters
                ? [
                      <Grid item key="object-filter-permission-notice">
                          <AcxChip
                              size="small"
                              color="gray"
                              label="Some filters are not applied, due to your permissions."
                              customMaterialIcon={
                                  <InfoOutlined
                                      sx={{
                                          fontSize: 16,
                                          color: theme.palette.gray.main,
                                      }}
                                  />
                              }
                              sx={{
                                  "& .MuiChip-label": {
                                      pr: 1.25,
                                  },
                              }}
                          />
                      </Grid>,
                  ]
                : undefined,
        [hasPermissionStrippedObjectFilters, theme.palette.gray.main],
    );

    const hasFilterChips = store.hasCommittedObjectFilters && !!objectFilterStore;

    if (!groupedByChipText && !hasFilterChips) {
        return null;
    }

    // One row, grouping first. The two are separate flex items rather than one chip flow because
    // only the filter chips are stripped from the PNG export -- `no-export` hides everything inside
    // it, so the grouping chip has to sit outside that box to survive.
    return (
        <Grid item xs={12} sx={{ mb: 1 }}>
            <Grid
                container
                direction="row"
                alignItems="flex-start"
                gap={0.5}
                flexWrap="wrap"
            >
                {groupedByChipText && (
                    <GroupedByChip text={groupedByChipText} />
                )}
                {hasFilterChips && objectFilterStore && (
                    <Grid
                        item
                        className="no-export"
                        sx={{ flex: "1 1 240px", minWidth: 0 }}
                    >
                        <AppliedFiltersChips
                            disabled
                            store={objectFilterStore}
                            filterItems={objectChipFilterItems}
                            allowedFilterKeys={objectChipAllowedFilterKeys}
                            renderDateRangeFirst
                            hideDateRangeChip={false}
                            appliedFilters={appliedObjectFiltersBaseline}
                            extraChips={permissionNoticeChip}
                        />
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
});

export default ObjectFiltersChips;
